import { useMemo } from 'react';
import type { AntwerpLandmark } from '../types/antwerp-landmark';
import { ANTWERP_CATEGORY_LABEL as CATEGORY_LABEL, ANTWERP_CATEGORY_COLOR as CATEGORY_COLOR } from '../types/antwerp-landmark';
import { isCustomPOI, type CustomPOI } from '../types/custom-poi';
import { useLandmarkStore } from '../state/landmarks';
import { EditableField } from './EditableField';

interface AntwerpLandmarkPanelProps {
  landmark: AntwerpLandmark;
  onClose: () => void;
}

export function AntwerpLandmarkPanel({ landmark: baseLandmark, onClose }: AntwerpLandmarkPanelProps) {
  const inArtistSelection = useLandmarkStore((state) => state.artistSelection.has(baseLandmark.id));
  const toggleArtistSelectionMember = useLandmarkStore((state) => state.toggleArtistSelectionMember);
  const customImage = useLandmarkStore((state) => state.customPOIImages[baseLandmark.id]);
  const openEditPOIForm = useLandmarkStore((state) => state.openEditPOIForm);
  const isAdmin = useLandmarkStore((state) => state.isAdmin);
  // Stable selector: returns undefined (not a fresh {}) when no override
  // exists. Zustand compares with Object.is — a fresh {} every render would
  // make this component re-render on every unrelated store update.
  const overrideFields = useLandmarkStore(
    (state) => state.landmarkOverrides[baseLandmark.id]?.fields,
  );
  const saveLandmarkOverride = useLandmarkStore((state) => state.saveLandmarkOverride);

  // Apply any saved admin edits on top of the baseline. Doing the merge here
  // means the panel re-renders on every override change without the parent
  // having to re-fire selectAntwerpLandmark (which caused render glitches).
  const landmark = useMemo<AntwerpLandmark>(
    () => (overrideFields ? ({ ...baseLandmark, ...overrideFields } as AntwerpLandmark) : baseLandmark),
    [baseLandmark, overrideFields],
  );

  const color = CATEGORY_COLOR[landmark.category];
  const label = CATEGORY_LABEL[landmark.category];
  const isCustom = isCustomPOI(landmark);
  const heroImageSrc = isCustom
    ? (customImage ?? null)
    : `/img/antwerp-historical/${landmark.id}.jpg`;

  // Helper bound to this landmark id. `null` reverts that field to baseline.
  const saveField = (field: string) => async (next: string | null) => {
    await saveLandmarkOverride(landmark.id, { [field]: next });
  };

  const isFieldOverridden = (field: string): boolean =>
    overrideFields !== undefined && Object.prototype.hasOwnProperty.call(overrideFields, field);

  return (
    <aside
      data-testid="antwerp-landmark-panel"
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: 'min(420px, 100vw)',
        background: 'var(--surface-raised)',
        borderLeft: '1px solid var(--border)',
        boxShadow: 'var(--shadow-elevated)',
        zIndex: 20,
        overflowY: 'auto',
        padding: 'var(--space-4)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        style={{
          position: 'absolute',
          top: 'var(--space-3)',
          right: 'var(--space-3)',
          width: 32,
          height: 32,
          background: 'transparent',
          color: 'var(--text)',
          fontSize: '1.2rem',
          cursor: 'pointer',
        }}
      >
        ×
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
        <span
          style={{
            display: 'inline-block',
            background: color,
            color: '#fffdf2',
            padding: '2px 8px',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.7rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </span>
        <EditableField
          value={landmark.period}
          label="Period"
          canEdit={isAdmin}
          isOverridden={isFieldOverridden('period')}
          multiline={false}
          onSave={(v) => saveField('period')(v)}
          onRevert={() => saveField('period')(null)}
        >
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{landmark.period}</span>
        </EditableField>
        {landmark.stillStands === false ? (
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
            no longer standing
          </span>
        ) : null}
      </div>

      <EditableField
        value={landmark.name}
        label="Name"
        canEdit={isAdmin}
        isOverridden={isFieldOverridden('name')}
        multiline={false}
        onSave={(v) => saveField('name')(v)}
        onRevert={() => saveField('name')(null)}
      >
        <h2
          style={{
            fontFamily: 'var(--font-serif)',
            color: 'var(--text-headline)',
            fontSize: '1.5rem',
            fontWeight: 700,
            lineHeight: 1.15,
            marginBottom: 'var(--space-2)',
            paddingRight: 'var(--space-8)',
          }}
        >
          {landmark.name}
        </h2>
      </EditableField>

      {landmark.alternativeNames && landmark.alternativeNames.length > 0 ? (
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 'var(--space-3)' }}>
          {landmark.alternativeNames.join(' · ')}
        </p>
      ) : null}

      {isAdmin ? (
        <button
          type="button"
          onClick={() => toggleArtistSelectionMember(landmark.id)}
          data-testid="artist-selection-toggle"
          aria-pressed={inArtistSelection}
          title={inArtistSelection ? 'Remove from artist selection' : 'Add to artist selection'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-3)',
            padding: '6px 12px',
            background: inArtistSelection ? color : 'var(--surface)',
            color: inArtistSelection ? '#fffdf2' : 'var(--text)',
            border: `1px solid ${inArtistSelection ? color : 'var(--border)'}`,
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <svg
            width={14}
            height={14}
            viewBox="0 0 24 24"
            fill={inArtistSelection ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          {inArtistSelection ? 'In artist selection' : 'Add to artist selection'}
        </button>
      ) : inArtistSelection ? (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-3)',
            padding: '4px 10px',
            background: color,
            color: '#fffdf2',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          <svg
            width={12}
            height={12}
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            focusable="false"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          In artist selection
        </div>
      ) : null}

      {heroImageSrc ? (
        <a
          href={heroImageSrc}
          target="_blank"
          rel="noopener noreferrer"
          title="Click to open full-size in a new tab"
          style={{ display: 'block', marginBottom: 'var(--space-3)' }}
        >
          <img
            src={heroImageSrc}
            alt={landmark.name}
            loading="lazy"
            onError={(e) => {
              (e.currentTarget.parentElement as HTMLAnchorElement).style.display = 'none';
            }}
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              cursor: 'zoom-in',
            }}
          />
        </a>
      ) : null}

      {isCustom && isAdmin ? (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-3)',
          }}
        >
          <button
            type="button"
            onClick={() => openEditPOIForm(landmark as CustomPOI)}
            data-testid="custom-poi-edit"
            style={{
              padding: '6px 12px',
              fontSize: '0.8rem',
              fontWeight: 600,
              background: 'var(--surface)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
            }}
          >
            Edit
          </button>
          <span
            style={{
              alignSelf: 'center',
              fontSize: '0.7rem',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            Synced from server
          </span>
        </div>
      ) : null}

      <div style={{ marginBottom: 'var(--space-3)' }}>
        <EditableField
          value={landmark.oneLiner}
          label="One-liner"
          canEdit={isAdmin}
          isOverridden={isFieldOverridden('oneLiner')}
          multiline
          onSave={(v) => saveField('oneLiner')(v)}
          onRevert={() => saveField('oneLiner')(null)}
        >
          <p
            style={{
              fontSize: '0.95rem',
              color: 'var(--text)',
              lineHeight: 1.45,
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              paddingRight: isAdmin ? 32 : 0,
            }}
          >
            {landmark.oneLiner}
          </p>
        </EditableField>
      </div>

      <div style={{ marginBottom: 'var(--space-4)' }}>
        <EditableField
          value={landmark.description}
          label="Description"
          canEdit={isAdmin}
          isOverridden={isFieldOverridden('description')}
          multiline
          onSave={(v) => saveField('description')(v)}
          onRevert={() => saveField('description')(null)}
        >
          <p style={{ fontSize: '0.9rem', lineHeight: 1.55, paddingRight: isAdmin ? 32 : 0 }}>
            {landmark.description}
          </p>
        </EditableField>
      </div>

      {(landmark.artistDirection || isAdmin) ? (
        <Section title="Artist direction" tone={color}>
          <EditableField
            value={landmark.artistDirection ?? ''}
            label="Artist direction"
            canEdit={isAdmin}
            isOverridden={isFieldOverridden('artistDirection')}
            multiline
            onSave={(v) => saveField('artistDirection')(v.length === 0 ? null : v)}
            onRevert={() => saveField('artistDirection')(null)}
          >
            <p style={{ fontSize: '0.9rem', lineHeight: 1.55, paddingRight: isAdmin ? 32 : 0 }}>
              {landmark.artistDirection ?? (
                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  (no artist direction yet)
                </span>
              )}
            </p>
          </EditableField>
        </Section>
      ) : null}

      {(landmark.folklore || isAdmin) ? (
        <Section title="Folklore" tone={color}>
          <EditableField
            value={landmark.folklore ?? ''}
            label="Folklore"
            canEdit={isAdmin}
            isOverridden={isFieldOverridden('folklore')}
            multiline
            onSave={(v) => saveField('folklore')(v.length === 0 ? null : v)}
            onRevert={() => saveField('folklore')(null)}
          >
            <p style={{ fontSize: '0.9rem', lineHeight: 1.55, paddingRight: isAdmin ? 32 : 0 }}>
              {landmark.folklore ?? (
                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  (no folklore yet)
                </span>
              )}
            </p>
          </EditableField>
        </Section>
      ) : null}

      {landmark.paintings && landmark.paintings.length > 0 ? (
        <Section title="Paintings & engravings" tone={color}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {landmark.paintings.map((p, i) => (
              <li key={i} style={{ fontSize: '0.85rem' }}>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--text)', fontWeight: 600 }}
                >
                  {p.artist} — {p.title}
                  {p.year ? ` (${p.year})` : ''} →
                </a>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {landmark.visualReferences && landmark.visualReferences.length > 0 ? (
        <Section title="Visual references" tone={color}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {landmark.visualReferences.map((img, i) => (
              <figure key={i} style={{ margin: 0 }}>
                <a href={img.src} target="_blank" rel="noopener noreferrer">
                  <img
                    src={img.src}
                    alt={img.caption}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                    }}
                  />
                </a>
                <figcaption style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>
                  {img.caption}
                  {img.source ? ` — ${img.source}` : ''}
                </figcaption>
              </figure>
            ))}
          </div>
        </Section>
      ) : null}

      {landmark.wikipedia ? (
        <p style={{ marginTop: 'var(--space-4)', fontSize: '0.85rem' }}>
          <a href={landmark.wikipedia} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text)', fontWeight: 600 }}>
            More on Wikipedia →
          </a>
        </p>
      ) : null}
    </aside>
  );
}

function Section({ title, tone, children }: { title: string; tone: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <h3
        style={{
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontWeight: 700,
          color: tone,
          marginBottom: 'var(--space-2)',
          paddingBottom: 'var(--space-1)',
          borderBottom: `2px solid ${tone}`,
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}
